import { nanoid } from "nanoid";

import * as notificationsApi from "@/apiv2/notifications";
import { createAlertAction } from "@/redux/alerts/alerts.actions";
import * as plannerActions from "@/redux/planner/planner.actions";
import * as plannerSelectors from "@/redux/planner/planner.selectors";
import { handleError } from "@/redux/utils/error";
import { parseApiError } from "@/utils/parseApiError";

import * as notificationsActions from "./notifications.actions";

const isAlreadyReadError = (error) => error?.response?.status === 404;

// handleError surfaces the error to the user via an alert and then rethrows. These
// thunks are dispatched fire-and-forget (no caller awaits or catches them), so the
// rethrow would surface as an unhandled promise rejection. The alert is enough here,
// so swallow the rethrow and let the thunk resolve.
const surfaceError = (error, dispatch) => {
  try {
    handleError(error, dispatch);
  } catch {
    // handleError only dispatches an alert for response-backed errors; for a
    // response-less failure (network/offline/CORS) it rethrows WITHOUT alerting.
    // Swallowing that here would make the failure silent, so surface a fallback
    // alert before suppressing the rethrow.
    if (!error?.response) {
      const { message, warnings } = parseApiError(error);
      dispatch(
        createAlertAction(nanoid(), message, true, "danger", undefined, undefined, undefined, warnings)
      );
    }
  }
};

export const markNotificationAsRead = (message) => async (dispatch, getState) => {
  try {
    if (message?.isCustomEvent) {
      await notificationsApi.markCustomEventReminderAsRead({ uid: message.id })();

      const reminders = plannerSelectors.getCustomEventReminders(getState());
      dispatch(
        plannerActions.setCustomEventReminders(
          reminders.map((r) => (r.id === message.id ? { ...r, isRead: true } : r))
        )
      );
    } else {
      await notificationsApi.markNotificationAsRead({ notification_id: message.id })();
      dispatch(notificationsActions.markChatNotificationRead(message.id));
    }
  } catch (error) {
    if (isAlreadyReadError(error)) {
      // A 404 means the server already considers it read; mirror the success-path
      // local update so the unread state does not go stale, for both types.
      if (message?.isCustomEvent) {
        const reminders = plannerSelectors.getCustomEventReminders(getState());
        dispatch(
          plannerActions.setCustomEventReminders(
            reminders.map((r) => (r.id === message.id ? { ...r, isRead: true } : r))
          )
        );
      } else {
        dispatch(notificationsActions.markChatNotificationRead(message.id));
      }
      return;
    }
    surfaceError(error, dispatch);
  }
};

export const markAllNotificationsAsRead = () => async (dispatch, getState) => {
  // allSettled (not all): the two endpoints are independent, so a failure of one
  // must not discard the local update for the one that already persisted server-side.
  const [chatResult, reminderResult] = await Promise.allSettled([
    notificationsApi.markAllNotificationsAsRead()(),
    notificationsApi.markAllCustomEventRemindersAsRead()(),
  ]);

  if (chatResult.status === "fulfilled") {
    dispatch(notificationsActions.markAllChatNotificationsRead());
  }

  if (reminderResult.status === "fulfilled") {
    const reminders = plannerSelectors.getCustomEventReminders(getState());
    dispatch(
      plannerActions.setCustomEventReminders(reminders.map((r) => ({ ...r, isRead: true })))
    );
  }

  const failure = [chatResult, reminderResult].find((r) => r.status === "rejected");
  if (failure) {
    surfaceError(failure.reason, dispatch);
  }
};

export const markAllPONotificationsAsRead = () => async (dispatch) => {
  try {
    await notificationsApi.markAllPONotificationsAsRead()();
  } catch (error) {
    surfaceError(error, dispatch);
  }
};
